import { createContext, useContext, type ReactNode } from "react";
import type { LayoutDensity } from "@/shared/hooks/useResponsiveSidebarLayout";
import type { SidebarLayoutMode } from "@/components/layout/sidebarLayout.const";

type AppShellSidebarContextValue = {
  /** True when the desktop sidebar is expanded (not icon rail). */
  desktopSidebarExpanded: boolean;
  sidebarMode: SidebarLayoutMode;
  layoutDensity: LayoutDensity;
  sidebarWidthPx: number;
};

const AppShellSidebarContext = createContext<AppShellSidebarContextValue>({
  desktopSidebarExpanded: false,
  sidebarMode: "collapsed",
  layoutDensity: "normal",
  sidebarWidthPx: 72,
});

export function AppShellSidebarProvider({
  desktopSidebarExpanded,
  sidebarMode = desktopSidebarExpanded ? "full-expanded" : "collapsed",
  layoutDensity = "normal",
  sidebarWidthPx = 72,
  children,
}: {
  desktopSidebarExpanded: boolean;
  sidebarMode?: SidebarLayoutMode;
  layoutDensity?: LayoutDensity;
  sidebarWidthPx?: number;
  children: ReactNode;
}) {
  return (
    <AppShellSidebarContext.Provider
      value={{ desktopSidebarExpanded, sidebarMode, layoutDensity, sidebarWidthPx }}
    >
      {children}
    </AppShellSidebarContext.Provider>
  );
}

/** Defaults to collapsed when outside AppLayout (e.g. public discover). */
export function useAppShellSidebarExpanded() {
  return useContext(AppShellSidebarContext).desktopSidebarExpanded;
}

export function useAppShellSidebarLayout() {
  return useContext(AppShellSidebarContext);
}
